import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  IconButton,
  Dropdown,
  IDropdownOption,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dialog,
  DialogType,
  DialogFooter,
  TooltipHost,
  Link,
} from '@fluentui/react';
import { dokumenteApi, dokumentTypenApi } from '../services/api';

interface IDokumentTyp {
  Id: number;
  Name: string;
  Farbe: string;
  IstSystem: boolean;
}

interface IDokument {
  Id: number;
  BestellungId: number;
  PositionId: number | null;
  DokumentTypId: number | null;
  DokumentTypName: string | null;
  DokumentTypFarbe: string | null;
  Dateiname: string;
  OriginalDateiname: string;
  ContentType: string;
  Dateigroesse: number;
  HochgeladenVon: string;
  HochgeladenVonName: string | null;
  HochgeladenAm: string;
}

interface IPosition {
  Id: number;
  PosID: number | string;
  Artikelname: string;
}

interface Props {
  bestellungId: number;
  positionen?: IPosition[];
  canUpload: boolean;
  canDeleteAll: boolean;
  currentUserEmail: string;
  onDokumentChange?: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

const MAX_SIZE = 20 * 1024 * 1024;

export const DokumentePanel: React.FC<Props> = ({
  bestellungId,
  positionen = [],
  canUpload,
  canDeleteAll,
  currentUserEmail,
  onDokumentChange,
}) => {
  const [dokumente, setDokumente]   = useState<IDokument[]>([]);
  const [typen, setTypen]           = useState<IDokumentTyp[]>([]);
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IDokument | null>(null);
  const [dragOver, setDragOver]     = useState(false);

  // Upload-State
  const [selectedFile, setSelectedFile]   = useState<File | null>(null);
  const [selectedTypId, setSelectedTypId] = useState<number | undefined>(undefined);
  const [selectedPosId, setSelectedPosId] = useState<number | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [docs, t] = await Promise.all([
        dokumenteApi.getByBestellung(bestellungId),
        dokumentTypenApi.getAll(),
      ]);
      setDokumente(Array.isArray(docs) ? docs : []);
      setTypen(Array.isArray(t) ? t : []);
    } catch {
      setError('Dokumente konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [bestellungId]);

  useEffect(() => { load(); }, [load]);

  const applyFile = (file: File | null) => {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      setError(`Datei zu groß (max. 20 MB). Aktuelle Größe: ${formatBytes(file.size)}`);
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyFile(e.target.files?.[0] || null);
  };

  // ── Drag & Drop Handler (Browser-Standard wird komplett unterdrückt) ──────
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0] || null;
    applyFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      await dokumenteApi.upload(bestellungId, selectedFile, {
        positionId:    selectedPosId,
        dokumentTypId: selectedTypId,
      });
      setSuccess(`"${selectedFile.name}" erfolgreich hochgeladen.`);
      setSelectedFile(null);
      setSelectedTypId(undefined);
      setSelectedPosId(undefined);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
      onDokumentChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Fehler beim Hochladen.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dokumenteApi.delete(deleteTarget.Id);
      setSuccess(`"${deleteTarget.OriginalDateiname}" gelöscht.`);
      setDeleteTarget(null);
      await load();
      onDokumentChange?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Fehler beim Löschen.');
      setDeleteTarget(null);
    }
  };

  const typOptionen: IDropdownOption[] = [
    { key: '', text: '— kein Typ —' },
    ...typen.map(t => ({ key: t.Id, text: t.Name })),
  ];

  const posOptionen: IDropdownOption[] = [
    { key: '', text: '— Bestellungs-Dokument —' },
    ...positionen.map(p => ({ key: p.Id, text: `Pos. ${p.PosID}: ${p.Artikelname}` })),
  ];

  const canDelete = (doc: IDokument) =>
    canDeleteAll || doc.HochgeladenVon?.toLowerCase() === currentUserEmail.toLowerCase();

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      {error   && <MessageBar messageBarType={MessageBarType.error}   onDismiss={() => setError(null)}>{error}</MessageBar>}
      {success && <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccess(null)}>{success}</MessageBar>}

      {/* Upload-Bereich */}
      {canUpload && (
        <Stack
          tokens={{ childrenGap: 8 }}
          styles={{ root: { background: '#f8f8f8', border: '1px solid #ddd', borderRadius: 4, padding: '12px 16px' } }}
        >
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Dokument hochladen</Text>

          {/* Drag & Drop Zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? '#0078d4' : '#c8c6c4'}`,
              borderRadius: 4,
              padding: '16px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragOver ? '#eff6fc' : 'white',
              transition: 'border-color 0.15s, background 0.15s',
              userSelect: 'none',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <Stack horizontal horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <span style={{ fontSize: 18 }}>📄</span>
                <Text styles={{ root: { fontWeight: 600, color: '#0078d4' } }}>{selectedFile.name}</Text>
                <Text variant="small" styles={{ root: { color: '#666' } }}>({formatBytes(selectedFile.size)})</Text>
                <IconButton
                  iconProps={{ iconName: 'Cancel' }}
                  title="Auswahl aufheben"
                  styles={{ root: { color: '#a4262c', height: 24, width: 24 } }}
                  onClick={e => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                />
              </Stack>
            ) : (
              <Stack tokens={{ childrenGap: 4 }}>
                <span style={{ fontSize: 28, display: 'block', color: dragOver ? '#0078d4' : '#c8c6c4' }}>⬆</span>
                <Text styles={{ root: { color: dragOver ? '#0078d4' : '#605e5c' } }}>
                  Datei hierher ziehen oder <span style={{ color: '#0078d4', textDecoration: 'underline' }}>klicken zum Auswählen</span>
                </Text>
                <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>Max. 20 MB</Text>
              </Stack>
            )}
          </div>

          {/* Typ + Position + Hochladen */}
          <Stack horizontal tokens={{ childrenGap: 8 }} wrap>
            <Stack.Item styles={{ root: { minWidth: 160 } }}>
              <Dropdown
                placeholder="Dokumenttyp"
                options={typOptionen}
                selectedKey={selectedTypId ?? ''}
                onChange={(_, opt) => setSelectedTypId(opt?.key ? Number(opt.key) : undefined)}
              />
            </Stack.Item>

            {positionen.length > 0 && (
              <Stack.Item styles={{ root: { minWidth: 220 } }}>
                <Dropdown
                  placeholder="Zuordnung (Position)"
                  options={posOptionen}
                  selectedKey={selectedPosId ?? ''}
                  onChange={(_, opt) => setSelectedPosId(opt?.key ? Number(opt.key) : undefined)}
                />
              </Stack.Item>
            )}

            <Stack.Item>
              <PrimaryButton
                text="Hochladen"
                iconProps={{ iconName: 'Upload' }}
                disabled={!selectedFile || uploading}
                onClick={handleUpload}
              />
              {uploading && <Spinner size={SpinnerSize.small} styles={{ root: { display: 'inline-block', marginLeft: 8 } }} />}
            </Stack.Item>
          </Stack>
        </Stack>
      )}

      {/* Dokumentliste */}
      {loading ? (
        <Spinner size={SpinnerSize.medium} label="Dokumente laden…" />
      ) : dokumente.length === 0 ? (
        <Text styles={{ root: { color: '#666', fontStyle: 'italic' } }}>
          Noch keine Dokumente vorhanden.
        </Text>
      ) : (
        <Stack tokens={{ childrenGap: 0 }}>
          {dokumente.map(doc => (
            <Stack
              key={doc.Id}
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
              styles={{
                root: {
                  borderBottom: '1px solid #eee',
                  padding: '8px 4px',
                  selectors: { ':hover': { background: '#f5f5f5' } },
                },
              }}
            >
              {/* Datei-Icon */}
              <Stack.Item styles={{ root: { width: 24, textAlign: 'center', color: '#0078d4', flexShrink: 0 } }}>
                <span>📄</span>
              </Stack.Item>

              {/* Dateiname + Link */}
              <Stack.Item grow>
                <Stack tokens={{ childrenGap: 2 }}>
                  <Link
                    href={dokumenteApi.getDownloadUrl(doc.Id)}
                    target="_blank"
                    styles={{ root: { fontWeight: 500 } }}
                  >
                    {doc.OriginalDateiname}
                  </Link>
                  <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" wrap>
                    {doc.DokumentTypName && (
                      <span
                        style={{
                          background: doc.DokumentTypFarbe || '#666',
                          color: '#fff',
                          borderRadius: 3,
                          padding: '1px 7px',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {doc.DokumentTypName}
                      </span>
                    )}
                    {doc.PositionId && (() => {
                      const pos = positionen.find(p => p.Id === doc.PositionId);
                      return pos ? (
                        <span style={{ background: '#f0f0f0', borderRadius: 3, padding: '1px 7px', fontSize: 11, color: '#333' }}>
                          Pos. {pos.PosID}: {pos.Artikelname}
                        </span>
                      ) : null;
                    })()}
                    <Text variant="tiny" styles={{ root: { color: '#999' } }}>
                      {formatBytes(doc.Dateigroesse)} · {doc.HochgeladenVonName || doc.HochgeladenVon} · {formatDate(doc.HochgeladenAm)}
                    </Text>
                  </Stack>
                </Stack>
              </Stack.Item>

              {/* Löschen */}
              {canDelete(doc) && (
                <Stack.Item styles={{ root: { flexShrink: 0 } }}>
                  <TooltipHost content="Dokument löschen">
                    <IconButton
                      iconProps={{ iconName: 'Delete' }}
                      styles={{ root: { color: '#a4262c' } }}
                      onClick={() => setDeleteTarget(doc)}
                    />
                  </TooltipHost>
                </Stack.Item>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {/* Lösch-Bestätigung */}
      <Dialog
        hidden={!deleteTarget}
        onDismiss={() => setDeleteTarget(null)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Dokument löschen',
          subText: `"${deleteTarget?.OriginalDateiname}" wirklich löschen?`,
        }}
      >
        <DialogFooter>
          <PrimaryButton text="Löschen" onClick={handleDelete} styles={{ root: { background: '#a4262c', border: 'none' } }} />
          <DefaultButton text="Abbrechen" onClick={() => setDeleteTarget(null)} />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};
